import styles from '../mna-info.module.scss';
import classnames from 'classnames/bind';
import React, {useState} from "react";
import {Form, Formik} from "formik";
import _ from 'lodash';
import * as Yup from 'yup';
import {useInjection} from "inversify-react";
import {CustomerInputApi} from "../../../model/customer/customer-input-api";
import {CustomerApi} from "../../../model/customer/customer-api";
import {DialogManager} from "../../../components/dialog-manager";
import {CustomerMnaBrokerageDto, EarningsEstimate} from "../../../model/customer/dto/customer-mna-brokerage-dto";
import MnaIntroductionFeePolicyPop from "./mna-introduction-fee-policy-pop";
import CompanyTypeForm from "./company-type-form";
import {alertComment, MnaInfoProp} from "../mna-info";
import {CustomerMnaNeedsDto} from "../../../model/customer/dto/customer-mna-needs-dto";
import {toast} from "react-toastify";
import {MnATypeList} from "../../../data/const_list";
import {CustomerMnaNeedsForm} from "../../../model/customer/form/customer-mna-needs-form";
import NeedsCustomerType from "./needs-customer-type";
import NeedsSaveBtn from "./needs-save-btn";

const cx = classnames.bind(styles);

const validationSchema = Yup.object({
    brokerage: Yup.object({
        mainProduct: Yup.string().required("필수 입력값입니다.").nullable(),
        bizCateDetail: Yup.string().required("필수 입력값입니다.").nullable(),
        mainSalesChannel: Yup.string().required("필수 입력값입니다.").nullable(),
        mainCompetitors: Yup.string().required("필수 입력값입니다.").nullable(),
        uniqueSellingPoints: Yup.string().required("필수 입력값입니다.").nullable(),
        synergisticPartners: Yup.string().required("필수 입력값입니다.").nullable(),
        salesReason: Yup.string().required("필수 입력값입니다.").nullable(),
        salesAmount: Yup.string().required("필수 입력값입니다.").nullable(),
        experRecvAcqOffer: Yup.string().required("필수 입력값입니다.").nullable(),
        shareHolderComposition: Yup.string().required("필수 입력값입니다.").nullable(),
        keyExecutives: Yup.string().required("필수 입력값입니다.").nullable(),
        planB: Yup.string().required("필수 입력값입니다.").nullable(),
    }),
    // businessName: Yup.lazy((value) => {
    //     if (Array.isArray(value)) {
    //         return Yup.array().of(Yup.string().required("필수 입력값입니다."));
    //     }
    //     return Yup.string().required("필수 입력값입니다.");
    // }),
    perform: Yup.array().of(
        Yup.object({
            sales: Yup.number().required("필수 입력").default(0),
            profit: Yup.number().required("필수 입력").default(0),
        })
    )
        // perform 배열 내에 sales 값이 하나라도 0보다 큰지 검사
        .test(
            "at-least-one-sales",
            "최소 하나의 예상 매출액은 0보다 커야 합니다.",
            function (value) {
                if (!value || value.length === 0) return false;
                return value.some(item => (item?.sales ?? 0) > 0);
            }
        )
        // perform 배열 내에 profit 값이 하나라도 0보다 큰지 검사
        .test(
            "at-least-one-profit",
            "최소 하나의 예상 영업이익은 0보다 커야 합니다.",
            function (value) {
                if (!value || value.length === 0) return false;
                return value.some(item => (item?.profit ?? 0) > 0);
            }
        )
});

const SellNeedsForm = ({data, needs, setData, setNeeds, needsType, setNeedsType, available} : MnaInfoProp) => {
    const customerInputApi = useInjection(CustomerInputApi);
    const customerApi = useInjection(CustomerApi);
    const dialogManager = useInjection(DialogManager);
    const [isDraft, setIsDraft] = useState(false);

    if (!needs) return <div></div>;

    const brokerage = needs.brokerage;

    // let parentCode = "";
    // let businessName = "";
    //
    // if (brokerage?.bizAreaInfo && brokerage.bizAreaInfo.length === 1 && brokerage.bizAreaInfo[0].parentCode === "기타") {
    //     businessName = brokerage.bizAreaInfo[0].parentCode;
    // } else {
    //     brokerage?.bizAreaInfo?.map((info) => {
    //         if(parentCode === "") {
    //             parentCode = info.parentCode;
    //         } else {
    //             parentCode += "," + info.parentCode;
    //         }
    //
    //         info.subCodes?.map((sub) => {
    //             if (businessName === "") {
    //                 businessName = sub;
    //             } else {
    //                 businessName += "," + sub;
    //             }
    //         });
    //     })
    // }

    const nowYear = new Date().getFullYear();

    const perform: EarningsEstimate[] = [
        {
            year: nowYear,
            sales: 0,
            profit: 0
        },
        {
            year: nowYear + 1,
            sales: 0,
            profit: 0
        },
        {
            year: nowYear + 2,
            sales: 0,
            profit: 0
        },
        {
            year: nowYear + 3,
            sales: 0,
            profit: 0
        },
        {
            year: nowYear + 4,
            sales: 0,
            profit: 0
        },
    ]

    const saveExcel = async (type: string) => {
        const shareMemberNo = data.share && data.share.srcMember.memberNo;
        const loadResult = await customerInputApi.excelMnaNeeds(data.customer!.id, shareMemberNo ?? "", type);

        if (loadResult.success) {
            window.location.href = customerApi.makeDownloadFile(loadResult.data!);
        } else {
            if (loadResult.message == "NoData") {
                dialogManager.alert("M&A 니즈 정보를 저장 후 다시 시도해주십시오.");
            } else {
                dialogManager.alert("상담시트 인쇄에 실패했습니다.");
            }
        }
    }

    const getEarningEstimates = (bk: CustomerMnaBrokerageDto | undefined) => {
        const tempEstimates = bk && Object.keys(bk).length !== 0 && bk.earningsEstimates && true && bk.earningsEstimates.length > 0 ? _.cloneDeep(bk.earningsEstimates) : [];

        return tempEstimates.length > 0 ?
            tempEstimates.map((ee) => {
                if (ee.sales !== 0)
                    ee.sales = ee.sales / 1e5;

                if (ee.profit !== 0)
                    ee.profit = ee.profit / 1e5;

                return ee;
            }) : perform;
    }

    return(
        <Formik
            initialValues={{
                companyType: needsType,
                perform: getEarningEstimates(brokerage),
                // businessName: businessName,
                brokerage: brokerage ?? {
                    mainProduct: '',
                    bizCateDetail: '',
                    mainSalesChannel: '',
                    mainCompetitors: '',
                    uniqueSellingPoints: '',
                    technicalDetails: '',
                    synergisticPartners: '',
                    salesReason: '',
                    salesAmount: '',
                    experRecvAcqOffer: '',
                    shareHolderComposition: '',
                    keyExecutives: '',
                    planB: '',
                },
            }}
            validationSchema={(isDraft || needsType === 'CHECK') ? null : validationSchema}
            onSubmit={async (values) => {
                const tempPerform = values.perform ? _.cloneDeep(values.perform) : [];

                const brokerage = {
                    ...values.brokerage,
                    earningsEstimates: tempPerform.map((value) => {
                        value.sales = value.sales * 1e5;
                        value.profit = value.profit * 1e5;
                        return value;
                    })
                }

                const form: CustomerMnaNeedsForm = {
                    shareMemberNo: data.share ? data.share.srcMember.memberNo : "",
                    isTempSave: needs.isTempSave,
                    isApplyNeeds: needs.isApplyNeeds,
                    enpNm: data.customer?.enpNm ?? "",
                    ceoName: data.customer?.rep[0].name ?? "",
                    type: needsType,
                    brokerage: brokerage,
                };

                const type = MnATypeList.find(it => it.id === needsType)?.title ?? '';

                if(isDraft) {
                    form.isTempSave = true;
                    form.isApplyNeeds = false;

                    const result = await customerInputApi.updateMnaNeeds(data.customer!.id, form);

                    if (result.success) {
                        toast.success('임시 저장되었습니다.');

                        const dto: CustomerMnaNeedsDto = {
                            id: data.customer!.id,
                            isTempSave: form.isTempSave,
                            isApplyNeeds: form.isApplyNeeds,
                            type: form.type,
                            brokerage: form.brokerage,
                            acq: form.acq,
                            createAt: needs.createAt
                        }

                        // data.customer!.companyInfo.mainProduct = form.brokerage?.mainProduct;

                        setNeeds(dto);
                        setData(data => {
                            return {
                                ...data
                            }
                        });
                    } else {
                        toast.error("임시 저장에 실패했습니다.");
                    }
                } else {
                    const chgTitle = () => {
                        let title: string = `${type} 기업으로 등록하시겠어요?`;
                        let content: string = '중개신청이 완료되면, 씨오M&A센터에서\n1영업일 이내 연락드립니다.';

                        if(needsType === 'CHECK') {
                            title = `매각 검토가능 기업으로 등록하시겠어요?`;
                            content = `매각검토가능 기업으로 등록해두시면,\n인수조건 매칭 시 우선적으로 연락드립니다.`;
                        }

                        return [title, content];
                    }

                    const reqMsgTitle = alertComment(needs, needsType) ? chgTitle()[0] : `저장하시겠습니까?`;

                    const reqMsgContent = alertComment(needs, needsType) ? chgTitle()[1] : '';

                    const cancelBtn = alertComment(needs, needsType) ? undefined : '취소';

                    dialogManager.confirm(reqMsgTitle, reqMsgContent, async () => {
                        form.isTempSave = false;
                        form.isApplyNeeds = true;

                        const result = await customerInputApi.updateMnaNeeds(data.customer!.id, form);

                        if (result.success) {
                            const rsMsg = alertComment(needs, needsType) ? "등록이 완료되었습니다." : "저장되었습니다.";
                            dialogManager.alert(rsMsg);

                            const dto: CustomerMnaNeedsDto = {
                                id: data.customer!.id,
                                type: form.type,
                                brokerage: form.brokerage,
                                createAt: needs.createAt
                            }

                            // data.customer!.companyInfo.mainProduct = form.brokerage?.mainProduct;

                            setNeeds((prevNeeds) => ({
                                ...prevNeeds,
                                id: dto.id,
                                type: dto.type,
                                brokerage: dto.brokerage,
                                isTempSave: form.isTempSave,
                                isApplyNeeds: form.isApplyNeeds,
                                createAt: dto.createAt
                            }));

                            setData(data => {
                                return {
                                    ...data
                                }
                            });
                        } else {
                            dialogManager.alert(result.message ?? "등록에 실패했습니다.");
                        }
                    }, undefined, undefined, undefined, cancelBtn);
                }

            }}>
            {({values}) =>

                <div className={cx('mna-info')}>
                    <Form>
                        <div className={cx('title-line')}>
                            <p className={cx('title')}>
                                M&A 니즈
                                <button className={cx('status-btn')} type='button' onClick={() => {dialogManager.open(MnaIntroductionFeePolicyPop, {})}}>M&A 소개수수료 정책<i></i></button>
                            </p>
                            <div className={cx('btn-area')}>
                                <button className={cx('print-btn')} type='button' onClick={() => saveExcel(values.companyType)}><i></i>상담시트 인쇄</button>
                                <NeedsSaveBtn needs={needs} needsType={needsType} setIsDraft={setIsDraft}/>
                                {/*{(needs.isTempSave === null || needs.isTempSave) && <button className={cx('temporary-save-btn')} type='submit' onClick={() => setIsDraft(true)}>임시 저장하기</button>}*/}
                                {/*<button className={cx('save-btn')} type='submit' onClick={() => setIsDraft(false)}>*/}
                                {/*    <i></i>{(needs.isTempSave !== null && !needs.isTempSave) ? "저장하기" : "등록하기"}*/}
                                {/*</button>*/}
                            </div>
                        </div>
                        <div className={cx('content')}>
                            <NeedsCustomerType needs={needs} needsType={needsType} setNeedsType={setNeedsType} available={available} />
                            <CompanyTypeForm data={data} needs={needs} info={values.brokerage?.bizAreaInfo ?? []} perform={values.perform ?? perform} setIsDraft={setIsDraft}/>
                        </div>
                    </Form>
                </div>
            }
        </Formik>
    );
}

export default SellNeedsForm;